import {
  iconButtonClasses,
  inputBaseClasses,
  inputLabelClasses,
  outlinedInputClasses,
  selectClasses,
} from '@mui/material'
import { Theme, alpha } from '@mui/material/styles'

import {
  icons,
  layout,
  semanticTokens,
  typography as typeTokens,
} from '../tokens.generated'
import { focusRingShadow } from './Button'

// ----------------------------------------------------------------------
// Inputs match buttons exactly (handover 4.2): the same three heights, the
// control radius, a hairline border and the accent focus ring. A button beside
// an input in a toolbar agrees with it on every edge.
//
// The height sits on the input element, with no block padding, so the text is
// centred by the box and not by a padding that has to be re-derived for every
// font size. The one exception is a multiline field, which has no height: its
// root carries the inset and the textarea grows inside it.
//
// The label is static text above the field, not MUI's floating label that
// notches the border. `MuiInputLabel` is always shrunk and laid out in flow,
// and the notch, the `legend` MUI cuts into the outline to make room for a
// floating label, is removed globally. A `Select` in a bare `FormControl`
// gets the same treatment, because it is the same label and the same outline.
// ----------------------------------------------------------------------

/**
 * Room for the caret before the first character of an input whose text starts
 * at its own edge, taken back as a negative margin so the text does not move.
 * Chrome paints the caret a pixel left of the text position, so at the start
 * of such an input the caret was clipped by the input's edge and only appeared
 * from the second letter (reported 2026-10-09 on the driver's Comments field).
 * See `layout.caretRoom`.
 */
export const caretRoom = {
  paddingInlineStart: layout.caretRoom,
  marginInlineStart: -layout.caretRoom,
}

export default function Input(theme: Theme) {
  const semantics = semanticTokens[theme.palette.mode]

  return {
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            '& svg': { color: theme.palette.text.disabled },
          },
        },
        input: {
          // The field's own corners, always. A browser that fills a field in
          // paints the input element, and the input is a square box inside a
          // rounded root, so the fill showed square corners outside the
          // field's edge. MUI rounds it only while Chrome's own autofill
          // state matches; a password manager's fill, or a browser that
          // paints without that state, got the square.
          'borderRadius': 'inherit',
          '&::placeholder': {
            opacity: 1,
            color: theme.palette.text.disabled,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: theme.palette.grey[500_56],
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          'backgroundColor': theme.palette.grey[500_12],
          // A field already sitting on a fill takes the step below it, which
          // is the same step every other control on a fill takes. This was a
          // 16% grey wash, a second spelling of a surface hover that nothing
          // else in the theme used.
          '&:hover': {
            backgroundColor: theme.hover.sunken,
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.action.focus,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
          },
        },
        underline: {
          '&:before': {
            borderBottomColor: theme.palette.grey[500_56],
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          'borderRadius': theme.radii.control,
          'backgroundColor': theme.palette.background.paper,
          'transition': theme.motion.state.transition(
            'box-shadow',
            'border-color',
          ),
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.grey[400],
          },
          [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.hover.edge,
          },
          '&.Mui-focused': {
            boxShadow: focusRingShadow(theme),
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.primary.main,
              borderWidth: 1,
            },
          },
          '&.Mui-error': {
            boxShadow: `0 0 0 ${layout.focusRingWidth}px ${alpha(
              theme.palette.error.main,
              0.2,
            )}`,
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.error.main,
            },
          },
          '&.Mui-disabled': {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
          // A value Beast owns, which nobody in this console can change.
          //
          // Not `disabled`. Disabled implies a path (*do something and this
          // comes alive*), so using it for a value that will never be the
          // provider's sends them hunting for a step that does not exist.
          // Rental terms showed this exactly: the panel copy said the fields
          // were not theirs to change while the fields looked editable, so the
          // copy was arguing with the controls.
          //
          // **Locked is flat, not dimmed.** The value is correct and still has
          // to be read (a provider quotes the clause and checks which body
          // hears a complaint), so the ink is the secondary text role and
          // never the disabled one. What changes is the ground and the
          // affordance: a sunken fill, a hairline instead of the input border,
          // and a cursor that says pressing will do nothing.
          //
          // Keyed off a data attribute rather than a class because MUI's
          // InputBase spreads unknown props onto its root, so a screen can
          // hand this down through `InputProps` and reach the element. The
          // same trick does *not* work on Switch, which spreads onto its
          // switchBase instead: see `components/FeatureRow.tsx`.
          '&[data-availability="locked"]': {
            backgroundColor: theme.palette.background.neutral,
            color: theme.palette.text.secondary,
            cursor: 'not-allowed',
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.divider,
            },
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.divider,
            },
            // The element that draws the value carries the affordance, not
            // just the root: an input and a textarea inherit the root's
            // cursor, and a select's value is a `div` with a `pointer` of its
            // own, which would have said *press me* on the one control whose
            // whole point is that pressing it does nothing.
            [`& input, & textarea, & .${selectClasses.select}`]: {
              cursor: 'not-allowed',
            },
          },
          // **A locked field shows the whole of its value.** Locked is flat
          // because the value is still read, but the field did not make room
          // for it: on a phone the vehicle form's locked VIN showed
          // `5YJ3E1EA7PF0`, twelve of its seventeen characters, because its
          // copy control and its lock took the rest of a field that shrank to
          // what the value column had left, and an input clips without
          // saying so. Nothing can be typed into a locked field, so its value
          // is the width it will always be: the input is sized to it
          // (`field-sizing: content`) and does not shrink, and the marks give
          // way instead, going under the value at its end. `max-width` holds
          // a value longer than the whole cell to the field.
          //
          // A single-line text field only, which is a root with an `input` of
          // its own. A select draws its value in a `div` the field's full
          // width with the lock in the chevron's place, so a wrapping select
          // would put its lock on a line of its own; a multiline field's
          // `textarea` is the field's width and already breaks its own lines.
          [`&[data-availability="locked"]:has(> input.${inputBaseClasses.input})`]:
            {
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              [`& > input.${inputBaseClasses.input}`]: {
                fieldSizing: 'content',
                width: 'auto',
                flexGrow: 1,
                flexShrink: 0,
                flexBasis: 'auto',
                maxWidth: '100%',
              },
            },
        },
        // The outline is a plain rectangle. MUI lifts it 5px to hide the
        // legend's line box behind the label; with the legend gone it sits on
        // the field's own edge.
        notchedOutline: {
          'top': 0,
          '& legend': {
            display: 'none',
          },
        },
        // The control inset is the dense inset: 12, one step under a button's
        // 16, because a field's text has no border of its own to sit against.
        input: {
          height: layout.controlHeight,
          boxSizing: 'border-box' as const,
          paddingBlock: 0,
          paddingInline: layout.controlInsetSmall,
        },
        inputSizeSmall: {
          height: layout.controlHeightSmall,
        },
        // A leading or trailing adornment moves the inset from the input to
        // the root, so the adornment sits at the inset and the text follows
        // it at the adornment's own gap.
        adornedStart: {
          paddingLeft: layout.controlInsetSmall,
        },
        adornedEnd: {
          paddingRight: layout.controlInsetSmall,
        },
        inputAdornedStart: {
          paddingLeft: 0,
          ...caretRoom,
        },
        inputAdornedEnd: {
          paddingRight: 0,
        },
        // Multiline: the root carries the inset and the textarea has none and
        // no height, so one line of text sits where a single-line field's
        // does and the field grows from there.
        multiline: {
          padding: theme.spacing(1.25, 1.5),
          [`&.${inputBaseClasses.sizeSmall}`]: {
            padding: theme.spacing(0.75, 1.5),
          },
        },
        inputMultiline: {
          height: 'auto',
          paddingInline: 0,
          ...caretRoom,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: theme.palette.text.disabled,
          // An icon button inside a field is an affordance of the field, not
          // a control of its own, so its box is its glyph. At the standalone
          // 36px square it stood 2px proud of a 32px dense field on every date
          // picker, sitting on the border it was inside. This is the same
          // reason the autocomplete indicators are reset.
          //
          // The hit area is restated rather than inherited: the one on the
          // IconButton root is an inset measured from the 36px square, and
          // from a 24px box that same inset would reach 32 and miss the touch
          // floor. Here it is measured from this box, to the same 44.
          [`& .${iconButtonClasses.root}`]: {
            'minWidth': icons.sizes.action,
            'minHeight': icons.sizes.action,
            '@media (pointer: coarse)': {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -(layout.controlHeightLarge - icons.sizes.action) / 2,
              },
            },
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        // Always shrunk: the label is never inside the field, so there is
        // nothing for it to float out of.
        shrink: true,
      },
      styleOverrides: {
        root: {
          'position': 'relative' as const,
          'transform': 'none',
          'transformOrigin': 'top left',
          'maxWidth': 'none',
          'overflow': 'visible',
          'whiteSpace': 'normal' as const,
          'pointerEvents': 'auto' as const,
          'fontSize': theme.typography.pxToRem(typeTokens.console.caption.size),
          'fontWeight': typeTokens.weights.semibold,
          'lineHeight': typeTokens.console.caption.lineHeight,
          'color': theme.palette.text.secondary,
          'marginBottom': theme.spacing(0.5),
          // The floating label changed colour on focus to say which field was
          // active. The ring says that now, and a static label that changes
          // colour reads as a validation state.
          '&.Mui-focused': {
            color: theme.palette.text.secondary,
          },
          '&.Mui-error': {
            color: semantics.error.text,
          },
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
          // The shrunk outlined placement is restated at its own depth, so
          // the label stays in flow whichever of MUI's rules is applied last.
          [`&.${inputLabelClasses.shrink}`]: {
            transform: 'none',
            maxWidth: 'none',
          },
        },
        asterisk: {
          color: theme.palette.error.main,
        },
      },
    },
  }
}
